import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Edit2, Send, Loader2, CheckCircle2, AlertTriangle, Sparkles, Plus, FileText, Tag, ShieldAlert,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';

const RESULTADO_OPTIONS = [
  { value: 'produtiva',    label: 'Produtiva',   color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'improdutiva',  label: 'Improdutiva', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'cancelada',    label: 'Cancelada',   color: 'bg-slate-100 text-slate-600 border-slate-300' },
  { value: 'reagendar',    label: 'Reagendar',   color: 'bg-blue-100 text-blue-700 border-blue-300' },
];

const RISK_COLORS = {
  high:     'bg-red-100 text-red-700 border-red-300',
  medium:   'bg-amber-100 text-amber-700 border-amber-300',
  low:      'bg-slate-100 text-slate-600 border-slate-300',
  critical: 'bg-red-200 text-red-900 border-red-400',
};

const CATEGORY_LABELS = {
  financial:    'financeiro',
  negotiation:  'negociação',
  contact:      'contato',
  timeline:     'prazo',
  legal:        'jurídico',
  operational:  'operacional',
  strategy:     'estratégia',
  risk:         'risco',
};

function toLocalInputDate(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const offsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 10);
}

export default function PostMeetingSection({ event, links = [], currentUserEmail, onChanged }) {
  const [phase, setPhase] = useState('idle'); // idle | writing | decomposing | done
  const [note, setNote] = useState('');
  const [resultado, setResultado] = useState('produtiva');
  const [proximoPasso, setProximoPasso] = useState('');
  const [proximaData, setProximaData] = useState('');
  const [error, setError] = useState(null);
  const [decomposeResult, setDecomposeResult] = useState(null);
  const [followUpId, setFollowUpId] = useState(null);

  const linkedRefs = useMemo(() => {
    const find = (type) => links.find((l) => l.entity_type === type) || null;
    return {
      empresa: find('empresa'),
      contato: find('contato'),
      oferta: find('oferta'),
    };
  }, [links]);

  const [resolved, setResolved] = useState({ empresa: null, contato: null, oferta: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const tasks = [];
      if (linkedRefs.empresa?.entity_id) {
        tasks.push(
          supabase.from('empresas').select('id, empresa, company_name').eq('id', linkedRefs.empresa.entity_id).maybeSingle()
            .then(({ data }) => ['empresa', data])
        );
      }
      if (linkedRefs.contato?.entity_id) {
        tasks.push(
          supabase.from('contatos').select('id, first_name, last_name, email').eq('id', linkedRefs.contato.entity_id).maybeSingle()
            .then(({ data }) => ['contato', data])
        );
      }
      if (linkedRefs.oferta?.entity_id) {
        tasks.push(
          supabase.from('ofertas').select('id, nome').eq('id', linkedRefs.oferta.entity_id).maybeSingle()
            .then(({ data }) => ['oferta', data])
        );
      }
      if (tasks.length === 0) return;
      const results = await Promise.all(tasks);
      if (cancelled) return;
      const next = { empresa: null, contato: null, oferta: null };
      for (const [key, val] of results) next[key] = val || null;
      setResolved(next);
    })();
    return () => { cancelled = true; };
  }, [linkedRefs.empresa?.entity_id, linkedRefs.contato?.entity_id, linkedRefs.oferta?.entity_id]);

  const empresaNome = resolved.empresa?.company_name || resolved.empresa?.empresa || null;
  const contatoNome = resolved.contato
    ? `${resolved.contato.first_name || ''} ${resolved.contato.last_name || ''}`.trim() || resolved.contato.email
    : null;
  const ofertaNome = resolved.oferta?.nome || null;

  const handleSubmit = useCallback(async () => {
    setError(null);
    if (!note.trim()) {
      setError('Escreva uma nota mínima sobre a reunião antes de analisar.');
      return;
    }
    // Onda 5.5 — sem entidade REIP, os fatos não conseguem ser salvos
    const hasContext = !!(linkedRefs.empresa?.entity_id || linkedRefs.oferta?.entity_id || linkedRefs.contato?.entity_id);
    if (!hasContext) {
      setError('Vincule este evento a uma empresa, oferta ou contato antes de analisar. Sem contexto, os fatos extraídos não podem ser salvos no grafo REIP. Use o botão "Vincular" na seção Contexto REIP acima.');
      return;
    }
    setPhase('decomposing');

    const annotatedNote = [
      `Resultado: ${resultado}`,
      proximoPasso ? `Próximo passo: ${proximoPasso}${proximaData ? ` (até ${proximaData})` : ''}` : null,
      '',
      note.trim(),
    ].filter(Boolean).join('\n');

    const { data, error: invokeErr } = await supabase.functions.invoke('decompose-meeting-notes', {
      body: {
        note: annotatedNote,
        context: {
          empresa_nome: empresaNome,
          empresa_id: linkedRefs.empresa?.entity_id || null,
          contato_nome: contatoNome,
          contato_id: linkedRefs.contato?.entity_id || null,
          oferta_nome: ofertaNome,
          oferta_id: linkedRefs.oferta?.entity_id || null,
          evento_titulo: event?.title_internal,
          evento_id: event?.id,
          colaborador_id: event?.owner_colaborador_id || null,
          colaborador_email: currentUserEmail || null,
        },
      },
    });

    if (invokeErr) {
      setError(invokeErr.message || 'Falha ao analisar a nota.');
      setPhase('writing');
      return;
    }
    if (!data?.success) {
      setError(data?.error || 'Edge Function retornou erro.');
      setPhase('writing');
      return;
    }

    // Follow-up opcional: cria scheduled_event tipo follow_up + link ao evento original
    if (proximoPasso.trim() && proximaData) {
      try {
        const startAt = new Date(proximaData);
        startAt.setHours(10, 0, 0, 0); // default 10h se só data
        const payload = {
          title_internal: `Follow-up: ${proximoPasso.trim()}`,
          description_internal: `Originado da reunião "${event?.title_internal || 'sem título'}".`,
          event_type: 'follow_up',
          status: 'scheduled',
          priority: 'medium',
          visibility: 'internal',
          start_at: startAt.toISOString(),
          end_at: null,
          all_day: false,
          owner_colaborador_id: event?.owner_colaborador_id || null,
          assigned_to_colaborador_id: event?.assigned_to_colaborador_id || null,
          business_unit_id: event?.business_unit_id || null,
          created_by_type: 'human',
          created_by_colaborador_id: event?.owner_colaborador_id || null,
          source: 'post_meeting',
        };
        const { data: newEv, error: insErr } = await supabase
          .from('scheduled_events')
          .insert(payload)
          .select('id')
          .single();
        if (!insErr && newEv?.id) {
          setFollowUpId(newEv.id);
          await supabase.from('scheduled_event_links').insert({
            scheduled_event_id: newEv.id,
            entity_type: 'scheduled_event',
            entity_id: event.id,
            relation_type: 'follow_up_of',
          });
          // Replicar vínculos de empresa/contato/oferta do evento original (best-effort)
          const replicaLinks = [];
          if (linkedRefs.empresa?.entity_id) replicaLinks.push({ scheduled_event_id: newEv.id, entity_type: 'empresa', entity_id: linkedRefs.empresa.entity_id, relation_type: 'related' });
          if (linkedRefs.contato?.entity_id) replicaLinks.push({ scheduled_event_id: newEv.id, entity_type: 'contato', entity_id: linkedRefs.contato.entity_id, relation_type: 'related' });
          if (linkedRefs.oferta?.entity_id) replicaLinks.push({ scheduled_event_id: newEv.id, entity_type: 'oferta', entity_id: linkedRefs.oferta.entity_id, relation_type: 'related' });
          if (replicaLinks.length > 0) await supabase.from('scheduled_event_links').insert(replicaLinks);
        }
      } catch (err) {
        console.warn('follow-up create failed:', err);
      }
    }

    setDecomposeResult(data);
    setPhase('done');
    onChanged?.();
  }, [note, resultado, proximoPasso, proximaData, empresaNome, contatoNome, ofertaNome, linkedRefs, event, currentUserEmail, onChanged]);

  // === Render ===

  if (phase === 'idle') {
    return (
      <div className="border-t border-slate-200 pt-4 mt-2">
        <button
          onClick={() => setPhase('writing')}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-100"
        >
          <Edit2 size={14} /> Registrar resultado da reunião
        </button>
      </div>
    );
  }

  if (phase === 'writing') {
    return (
      <div className="border-t border-slate-200 pt-4 mt-2 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-emerald-600" />
          <span className="text-sm font-semibold text-slate-800">Registrar resultado</span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700 flex items-start gap-1.5">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            O que foi discutido? Decisões, próximos passos, observações...
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={5}
            placeholder="Reunião com Cyrela sobre terreno Moema. Pediram R$15M, vamos contra-propor R$12M..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            autoFocus
          />
          <div className="text-[10px] text-slate-400 mt-1">
            A nota vai virar átomos de conhecimento via <code>agent_note_decomposer</code>.
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Resultado</label>
          <div className="flex flex-wrap gap-1.5">
            {RESULTADO_OPTIONS.map((r) => (
              <button
                key={r.value}
                onClick={() => setResultado(r.value)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  resultado === r.value ? r.color : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Próximo passo (opcional)</label>
            <input
              type="text"
              value={proximoPasso}
              onChange={(e) => setProximoPasso(e.target.value)}
              placeholder="Ex: enviar contraproposta"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Data do próximo passo</label>
            <input
              type="date"
              value={proximaData}
              onChange={(e) => setProximaData(e.target.value)}
              min={toLocalInputDate(new Date())}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => { setPhase('idle'); setError(null); }}
            className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!note.trim()}
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Send size={14} /> Salvar e analisar
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'decomposing') {
    return (
      <div className="border-t border-slate-200 pt-4 mt-2">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Loader2 size={16} className="animate-spin text-emerald-600" />
          Analisando nota com <code className="text-xs">agent_note_decomposer</code>...
        </div>
      </div>
    );
  }

  // phase === 'done'
  const r = decomposeResult || {};
  const facts = Array.isArray(r.facts) ? r.facts : [];
  const high = facts.filter((f) => ['high', 'critical'].includes((f.risk_level || '').toLowerCase()));

  return (
    <div className="border-t border-slate-200 pt-4 mt-2 space-y-3">
      <div className="flex items-center gap-2">
        <CheckCircle2 size={16} className="text-emerald-600" />
        <span className="text-sm font-semibold text-slate-800">
          {facts.length} {facts.length === 1 ? 'fato extraído' : 'fatos extraídos'}
          {high.length > 0 && (
            <span className="ml-2 text-red-600">· {high.length} de alto risco</span>
          )}
        </span>
      </div>

      {r.evento_atualizado && (
        <div className="text-xs text-emerald-700 flex items-center gap-1.5">
          <CheckCircle2 size={12} /> Evento marcado como <strong>concluído</strong>.
        </div>
      )}
      {r.historico_id && (
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <FileText size={12} /> Registro adicionado em <code>historico_interacoes</code>.
        </div>
      )}
      {followUpId && (
        <div className="text-xs text-blue-700 flex items-center gap-1.5">
          <Plus size={12} /> Follow-up criado na agenda{proximaData ? ` para ${proximaData}` : ''}.
        </div>
      )}

      {facts.length === 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          Nenhum fato estruturado foi extraído. A nota foi salva no histórico mesmo assim.
        </div>
      )}

      {facts.length > 0 && (
        <ul className="space-y-1.5">
          {facts.map((f, i) => {
            const cat = CATEGORY_LABELS[f.category] || f.category || 'geral';
            const risk = (f.risk_level || '').toLowerCase();
            const riskCls = RISK_COLORS[risk] || null;
            return (
              <li key={i} className="bg-slate-50 border border-slate-200 rounded p-2 flex items-start gap-2 text-xs">
                <CheckCircle2 size={12} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-800 break-words">{f.content}</div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-300 text-slate-600">
                      <Tag size={9} /> {cat}
                    </span>
                    {riskCls && (
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${riskCls}`}>
                        <ShieldAlert size={9} /> {risk}
                      </span>
                    )}
                    {typeof f.confidence === 'number' && (
                      <span className="text-[10px] text-slate-400">conf. {(f.confidence * 100).toFixed(0)}%</span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {Array.isArray(r.warnings) && r.warnings.length > 0 && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 space-y-0.5">
          <div className="flex items-center gap-1.5 font-medium">
            <AlertTriangle size={11} /> Avisos
          </div>
          {r.warnings.map((w, i) => (
            <div key={i}>· <code>{w.step}</code>: {w.error}</div>
          ))}
        </div>
      )}
    </div>
  );
}
